import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  formatDateToShortDotV2,
  applyTypography,
  BaseButton,
  isWithin24Hours,
  Checkbox,
  JOB_CARD_TYPE,
  TITLE_CHIP_MAP,
  STATUS_CHIP_MAP,
  useIsMobile,
  useT,
} from '@repo/common';
import CardOptionList from './CardOptionList';
import JobCompanyLogo from '../JobCompanyLogo';

import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';

const AdminJobCard = ({ job, className = '', checked, onToggle, onAction }) => {
  const t = useT();
  const navigate = useNavigate();
  const {
    id,
    title,
    company,
    companyLogoUrl,
    location,
    hasE7Support,
    employmentType,
    keywords,
    titleChips,
    status,
    dday,
    createdAt,
    modifiedAt,
  } = job;
  const [showOptions, setShowOptions] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const isDeletedFromActiveOrHidden =
    status === JOB_CARD_TYPE.DELETED_FROM_ACTIVE ||
    status === JOB_CARD_TYPE.DELETED_FROM_HIDDEN;
  const displayKeywords = (keywords || []).filter(
    (keyword) => keyword && keyword !== employmentType
  );

  const isMobile = useIsMobile();

  const openOptions = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setShowOptions(true);
  };

  const optionList = showOptions && (
    <CardOptionList
      anchorRect={anchorRect}
      onClose={() => setShowOptions(false)}
      status={job.status}
      onAction={(action) => {
        onAction?.(action, job);
        setShowOptions(false);
      }}
    />
  );

  /* 모바일: 체크박스 없이 상태칩 → 제목 → 회사/지역 → 18px 칩 행 (레이아웃이 통째로 달라 JS 분기) */
  if (isMobile) {
    const mobileChipClass =
      'inline-flex h-[18px] shrink-0 items-center justify-center gap-1 rounded-md bg-grayscale-gray01 px-1 py-px typo-b11 whitespace-nowrap';

    return (
      <div
        className={`group relative flex w-full cursor-pointer flex-col items-start overflow-clip rounded-[20px] border border-grayscale-gray02 bg-grayscale-gray00 px-4 py-[14px] transition-colors active:bg-green-light01 ${className}`}
        onClick={() => navigate(`/jobs/admin/${id}`)}
      >
        <div className='flex w-full items-start gap-[10px]'>
          <div className='flex min-w-0 flex-[1_0_0] flex-col items-start gap-2'>
            {(isWithin24Hours(createdAt) || STATUS_CHIP_MAP[status]) && (
              <div className='flex w-full items-center gap-1.5'>
                {isWithin24Hours(createdAt) && (
                  <span className={`${mobileChipClass} border border-grayscale-gray05 text-text-secondary`}>
                    {t('New', '신규')}
                  </span>
                )}
                {STATUS_CHIP_MAP[status] &&
                  (isDeletedFromActiveOrHidden ? (
                    <span className='typo-b11 text-text-secondary'>
                      {STATUS_CHIP_MAP[status].label}
                    </span>
                  ) : (
                    <span
                      className={`${mobileChipClass} ${
                        STATUS_CHIP_MAP[status].className || ''
                      }`}
                    >
                      {STATUS_CHIP_MAP[status].label}
                    </span>
                  ))}
              </div>
            )}
            <p className='w-full line-clamp-2 typo-b16Strong text-text-primary transition-colors group-active:text-green-deep03'>
              {title}
            </p>
          </div>

          <div
            className='relative flex shrink-0 flex-col items-center justify-center'
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type='button'
              aria-label={t('Job options', '공고 옵션')}
              className='flex items-center justify-center p-[2px] text-icon-placeholder active:text-icon-primary'
              onClick={openOptions}
            >
              <OptionIcon className='size-5' />
            </button>
            {optionList}
          </div>
        </div>

        <div className='flex w-full items-center gap-2'>
          {companyLogoUrl && (
            <div className='flex shrink-0 items-center py-2'>
              <JobCompanyLogo
                src={companyLogoUrl}
                sizeClassName='size-[26px] rounded-[4px]'
                padding='p-1'
              />
            </div>
          )}
          <div className='flex min-w-0 flex-[1_0_0] items-center gap-2 py-2'>
            <p className='truncate typo-b13 text-text-placeholder'>{company}</p>
            <div className='size-[3px] shrink-0 rounded bg-icon-placeholder' />
            <p className='truncate typo-b13 text-text-placeholder'>{location}</p>
          </div>
        </div>

        <div className='flex w-full flex-wrap content-start items-start gap-2'>
          {modifiedAt && (
            <span className={`${mobileChipClass} text-text-secondary`}>
              Last updated {formatDateToShortDotV2(modifiedAt)}
            </span>
          )}
          {dday && (
            <span className={`${mobileChipClass} text-purple-purple04`}>
              {dday}
            </span>
          )}
          {(titleChips || []).map((chip) => {
            const meta = TITLE_CHIP_MAP[chip];
            if (!meta) return null;

            return (
              <span
                key={chip}
                className={`${mobileChipClass} text-purple-purple04 [&>svg]:size-4 [&>svg]:shrink-0`}
              >
                {meta.icon}
                {meta.label}
              </span>
            );
          })}
          {hasE7Support && (
            <span className={`${mobileChipClass} text-green-deep04`}>
              E-7 Support
            </span>
          )}
          {employmentType && (
            <span className={`${mobileChipClass} text-text-secondary`}>
              {employmentType}
            </span>
          )}
          {displayKeywords.map((keyword, idx) => (
            <span
              key={`${keyword}-${idx}`}
              className={`${mobileChipClass} text-text-secondary`}
            >
              {keyword}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='flex w-full items-start'>
      <div className='w-7 shrink-0 pt-6'>
        <Checkbox checked={checked} onChange={(e) => onToggle(e.target.checked)} />
      </div>
    <div
      className={`group relative bg-grayscale-gray00 active:bg-green-light01
        border border-grayscale-gray02 hover:border-green-deep02
        transition-all duration-200 ease-out flex flex-row items-start
        min-h-[146px] min-w-0 overflow-clip rounded-lg w-full ${className}`}
      onClick={() => {
        navigate(`/jobs/admin/${id}`);
      }}
    >
      <div className='flex flex-col gap-6 items-start w-full p-6 pr-2'>
        <div className='flex gap-4 items-start w-full cursor-pointer'>
          <div className='flex flex-[1_0_0] gap-4 items-start min-h-px min-w-px'>
            <JobCompanyLogo
              src={companyLogoUrl}
              sizeClassName='size-[50px]'
              textClassName={applyTypography('b12')}
            />

            <div className='flex flex-[1_0_0] flex-col gap-1.5 items-start justify-center min-h-[50px] min-w-px'>
              {/* Title */}
              <p
                className={`${applyTypography('title3')}
                 text-text-primary group-hover:text-green-deep03 transition-colors duration-200
                 overflow-hidden relative shrink-0 text-ellipsis`}
              >
                {title}
              </p>

              {/* Company and Location */}
              <div className='flex gap-2 items-center relative shrink-0'>
                <p
                  className={`${applyTypography(
                    'b14'
                  )} relative shrink-0 text-text-placeholder`}
                >
                  {company}
                </p>
                <div className='bg-icon-placeholder rounded shrink-0 size-[3px]' />
                <p
                  className={`${applyTypography(
                    'b14'
                  )} relative shrink-0 text-text-placeholder`}
                >
                  {location}
                </p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div
            className='flex gap-0.5 items-center relative shrink-0'
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <>
              <div
                className='flex gap-2 items-center relative shrink-0'
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {isWithin24Hours(createdAt) && (
                  <BaseButton
                    type='text'
                    size='s'
                    hideIcon
                    bgclass={`px-1.5 py-1 h-6 border-grayscale-gray05 bg-grayscale-gray00 text-grayscale-gray05`}
                    textclass='typo-b12Strong'
                    width='w-fit'
                  >
                    {t('New', '신규')}
                  </BaseButton>
                )}
                {STATUS_CHIP_MAP[status] &&
                  (isDeletedFromActiveOrHidden ? (
                    <span className='typo-b12 text-text-secondary'>
                      {STATUS_CHIP_MAP[status].label}
                    </span>
                  ) : (
                    <BaseButton
                      type='text'
                      size='s'
                      hideIcon
                      bgclass={`px-1.5 py-1 h-6 border-none ${
                        STATUS_CHIP_MAP[status].className || ''
                      }`}
                      textclass='typo-b12Strong'
                    >
                      {STATUS_CHIP_MAP[status].label}
                    </BaseButton>
                  ))}
              </div>
              <BaseButton
                type='text'
                size='s'
                className='h-auto min-w-0 rounded-none border-0 bg-transparent hover:bg-transparent text-icon-placeholder hover:text-icon-primary p-[10px]'
                icon={<OptionIcon />}
                onClick={openOptions}
              />
              {optionList}
            </>
          </div>
        </div>

        {/* Tags */}
        <div className='flex flex-wrap gap-2 items-start relative shrink-0 w-full'>
          {modifiedAt && (
            <BaseButton
              type='text'
              hideIcon
              bgclass='h-6 bg-grayscale-gray01 py-1.5 px-3'
              textclass='text-text-secondary typo-b13'
              width='w-fit'
            >
              Last updated {formatDateToShortDotV2(modifiedAt)}
            </BaseButton>
          )}
          {dday && (
            <BaseButton
              type='text'
              hideIcon
              bgclass='h-6 bg-grayscale-gray01 py-1.5 px-3'
              textclass='text-purple-purple04 typo-b13'
              width='w-fit'
            >
              {dday}
            </BaseButton>
          )}
          {titleChips.map((chip) => {
            const meta = TITLE_CHIP_MAP[chip];
            if (!meta) return null;

            return (
              <BaseButton
                key={chip}
                type='text'
                size='s'
                icon={meta.icon}
                gap='gap-0.5'
                bgclass='h-6 bg-grayscale-gray01 py-1 pl-1 pr-[6px]'
                textclass='text-purple-purple04 typo-b12'
              >
                {meta.label}
              </BaseButton>
            );
          })}
          {hasE7Support && (
            <BaseButton
              type='text'
              hideIcon
              bgclass='px-3 py-[6px] bg-grayscale-gray01 border-none h-6'
              textclass='text-green-deep03 typo-b13'
            >
              E-7 Support
            </BaseButton>
          )}
          <BaseButton
            type='text'
            hideIcon
            bgclass='px-3 py-[6px] h-6'
            textclass='typo-b13'
          >
            {employmentType}
          </BaseButton>
          {displayKeywords.map((keyword, idx) => (
            <BaseButton
              key={`${keyword}-${idx}`}
              type='text'
              hideIcon
              bgclass='px-3 py-[6px] h-6'
              textclass='typo-b13'
            >
              {keyword}
            </BaseButton>
          ))}
        </div>
      </div>
    </div>
    </div>
  );
};

export default AdminJobCard;
